import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery, useApolloClient } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  UserTooltip,
  isNumeric,
  toastSuccess,
  FormCol,
  YupStringReq,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput } from '../graphql';
import { FormFields } from './form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { LegacyTooltip } from 'shared-pages/components/legacy-tooltip';
import { Tab, Tabs } from 'react-bootstrap';
import { VehiclesGridPage } from 'shared-pages/pages/vehicles';
import { DriversGridPage } from 'shared-pages/pages/drivers';
import { RowFieldTooltips } from 'shared-pages/components';
import { isFotokiklosi, isRebattery } from 'shared-pages/common/constants';
import { ContributionsGridPage } from '../../contributions';
import { getRouteByName } from 'app/routes';

export const ContributorFormPage = () => {
  const { handleGraphqlError } = useHandleError('Contributor');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id } = useParams<{ id: string }>();
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    name: YupStringReq,
    addressLine1: YupStringReq,
    addressLine2: YupString,
    addressArea: YupStringReq,
    addressCity: YupStringReq,
    postalCode: YupStringReq,
    contactPerson: YupString,
    phoneNumber1: YupStringReq,
    phoneNumber2: YupString,
    mobileNumber1: YupString,
    email: YupStringReq,
    vatNumber: YupStringReq,
    taxOffice: YupStringReq,
    producerRegNum: YupString,
    isActive: YupBoolean,
    hasBatteriesGroup1: YupBoolean,
    hasBatteriesGroup2: YupBoolean,
    contributorGroupId: YupNumeric,
    contributionPeriodGroupId: YupNumeric,
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const newRecord = useMemo<FormValuesType>(
    () =>
      ({
        id: -1,
        isActive: true,
      } as FormValuesType),
    [],
  );

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = isNew ? newRecord : rawData ? rawData[Object.keys(rawData)[0]] : undefined;

  const tabStyle = { minHeight: '360px' };
  const [key, setKey] = useState('contributions');

  /* fetch data */
  useEffect(() => {
    get({ variables: { id: idNum } });
  }, [get, id, idNum]);

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Contributors')), { ...location.state });
  }, [history, location.state]);

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
    // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
    console.log('[Applog] Update:', mutValues);
    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[IDoc] Add:', mutValues);
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${rec?.id}]`);
          handleReturn();
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });
    } else {
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <>
        <FormCard
          title={isNew ? `${t('Δήλωση Νέου Υπόχρεου')}` : `${t('Υπόχρεος')}: ${data?.name}`}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onReturn={handleReturn}
          permitSubmit={false}
          permitDelete={false}
          initialValues={pickProperties(data)}
          validationSchema={validationSchema}
          enableReinitialize
          idx={idNum}
        >
          <FormFields isNew={isNew} updateRecord={updateRecord} refetch={refetch} data={data} />
          {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
        </FormCard>
        {data && data.id > 0 && (
          <div>
            <Tabs activeKey={key} onSelect={(k) => setKey(k)}>
              <Tab eventKey="contributions" title={t('Δηλώσεις Εισφορών')} style={tabStyle}>
                {key == 'contributions' && (
                  <ContributionsGridPage
                    contributorId={data.id}
                    isGridInForm={true}
                  ></ContributionsGridPage>
                )}
              </Tab>
            </Tabs>
          </div>
        )}
      </>
    );
  }
};

